import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Save, Send, X, FileText, Upload, Eye, Images, Trash2, Edit2, User, Calendar, MessageSquare } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { toast } from "sonner";
import { Memo } from "../types";

type UploadedImage = {
  file: File;
  preview: string;
  caption: string;
};

export default function MemoNewPage() {
  const { currentUser } = useAuth();
  const { users, memos, addMemo, fetchMemos, approvers, fetchApprovers } = useData();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    number: '',
    subject: '',
    requireSignature: false,
    effectiveDate: '',
    expiryDate: '',
    approvedBy: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [previewMode, setPreviewMode] = useState(false);

  // Helper function to clear specific errors
  const clearError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // AI Enhancement State
  const [aiState, setAiState] = useState({
    isLoading: false,
    showPreview: false,
    currentField: '',
    currentAction: '',
    result: '',
    originalValue: ''
  });

  // Keyboard shortcuts for AI enhancements
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) {
        switch (event.key.toLowerCase()) {
          case 'p':
            event.preventDefault();
            // Determine which field to polish based on current focus or last active field
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement?.id === 'subject') {
              handleAiEnhancement('subject', 'polish');
            } else if (activeElement?.id === 'content') {
              handleAiEnhancement('content', 'polish');
            } else {
              // Default to content if no specific field is focused
              handleAiEnhancement('content', 'polish');
            }
            break;
          case 'g':
            event.preventDefault();
            // Determine which field to generate based on current focus
            const activeEl = document.activeElement as HTMLElement;
            if (activeEl?.id === 'content') {
              handleAiEnhancement('content', 'generate');
            } else {
              // Default to content if no specific field is focused
              handleAiEnhancement('content', 'generate');
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [formData.subject, content, aiState.isLoading]);

  // Generate next memo number
  const generateNextMemoNumber = () => {
    if (memos.length === 0) return `MEMO-${new Date().getFullYear()}-001`;

    // Find the highest number used in the current year specifically
    const currentYear = new Date().getFullYear();
    let highestNumberInCurrentYear = 0;

    memos.forEach(memo => {
      const match = memo.number.match(/MEMO-(\d{4})-(\d{3})/);
      if (match) {
        const year = parseInt(match[1], 10);
        const num = parseInt(match[2], 10);

        // Only consider memos from the current year
        if (year === currentYear && num > highestNumberInCurrentYear) {
          highestNumberInCurrentYear = num;
        }
      }
    });

    const nextNumber = highestNumberInCurrentYear + 1;
    return `MEMO-${currentYear}-${nextNumber.toString().padStart(3, '0')}`;
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, [uploadedImages]);

  // Auto-generate memo number on component mount
  useEffect(() => {
    // Fetch all memos including deleted ones for accurate number generation
    fetchMemos();
    if (approvers.length === 0) {
      fetchApprovers();
    }
  }, []);

  useEffect(() => {
    if (!formData.number) {
      const nextNumber = generateNextMemoNumber();
      setFormData(prev => ({ ...prev, number: nextNumber }));
    }
  }, [memos]);

  // Handle effective date change and clear dependent dates if needed
  const handleEffectiveDateChange = (newEffectiveDate: string) => {
    const updatedFormData = { ...formData, effectiveDate: newEffectiveDate };

    // If effective date is cleared, clear expiry date
    if (!newEffectiveDate) {
      updatedFormData.expiryDate = "";
    } else {
      // If new effective date is after current expiry date, clear it
      if (formData.expiryDate && new Date(formData.expiryDate) <= new Date(newEffectiveDate)) {
        updatedFormData.expiryDate = "";
      }
    }

    setFormData(updatedFormData);

    // Clear related errors if they exist
    const newErrors = { ...errors };
    if (errors.effectiveDate) {
      delete newErrors.effectiveDate;
    }
    if (errors.expiryDate && !newEffectiveDate) {
      delete newErrors.expiryDate;
    }
    setErrors(newErrors);
  };

  // AI Enhancement Functions
  const callChatGPT = async (prompt: string, content: string): Promise<string> => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/ai/enhance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt,
          content
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get AI enhancement');
      }

      const data = await response.json();
      return data.result;
    } catch (error) {
      console.error('AI Enhancement Error:', error);
      throw error;
    }
  };

  const handleAiEnhancement = async (field: string, action: string) => {
    let prompt = '';
    let contentToEnhance = '';
    let originalValue = '';

    // Determine prompt and content based on field and action
    switch (field) {
      case 'subject':
        if (action === 'polish') {
          prompt = "Polish the following memo subject. Keep it professional, concise, and clear. Do not use markdown formatting or special characters.";
          contentToEnhance = formData.subject;
          originalValue = formData.subject;
        }
        break;

      case 'content':
        if (action === 'polish') {
          prompt = "Polish the following memo content. Make it more clear, professional, and well-structured. Improve grammar and flow. Use appropriate business memo format.";
          contentToEnhance = content;
          originalValue = content;
        } else if (action === 'generate') {
          prompt = `Generate professional memo content based on this subject: "${formData.subject}". Create a well-structured business memo with appropriate sections. Use formal business language.`;
          contentToEnhance = formData.subject;
          originalValue = content;
        }
        break;
    }

    if (!contentToEnhance.trim()) {
      toast.error(`Please enter ${field === 'subject' ? 'a subject' : 'content'} first`);
      return;
    }

    setAiState({
      isLoading: true,
      showPreview: false,
      currentField: field,
      currentAction: action,
      result: '',
      originalValue
    });

    try {
      const result = await callChatGPT(prompt, contentToEnhance);

      setAiState({
        ...aiState,
        isLoading: false,
        showPreview: true,
        result
      });
    } catch (error) {
      console.error('AI Enhancement Error:', error);
      setAiState({
        ...aiState,
        isLoading: false,
        showPreview: false
      });

      const errorMessage = error instanceof Error ? error.message : 'Failed to enhance content';
      toast.error(errorMessage);
    }
  };

  const handleAiAccept = () => {
    const { currentField, result } = aiState;

    switch (currentField) {
      case 'subject':
        setFormData({ ...formData, subject: result });
        break;
      case 'content':
        setContent(result);
        break;
    }

    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });

    toast.success('Enhancement applied successfully!');
  };

  const handleAiCancel = () => {
    setAiState({
      isLoading: false,
      showPreview: false,
      currentField: '',
      currentAction: '',
      result: '',
      originalValue: ''
    });
  };

  const validateForm = (isDraft = false) => {
    const newErrors: Record<string, string> = {};

    // Required fields for both draft and published memos
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!content.trim()) newErrors.content = "Content is required";
    if (!formData.effectiveDate) newErrors.effectiveDate = "Effective date is required";

    // Additional validation for published memos
    if (!isDraft) {
      if (!formData.approvedBy) newErrors.approvedBy = "Approver is required for published memos";
    }

    // Date validations
    if (formData.effectiveDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.effectiveDate)) {
        newErrors.expiryDate = "Expiry date must be after effective date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (status: 'Draft' | 'Active') => {
    const isValid = validateForm(status === 'Draft');
    if (!isValid || !currentUser) return;

    setIsSaving(true);
    try {
      const memoData: Memo = {
        id: Date.now().toString(),
        number: formData.number,
        subject: formData.subject,
        status: status,
        content: content,
        requireSignature: formData.requireSignature,
        effectiveDate: formData.effectiveDate,
        expiryDate: formData.expiryDate || undefined,
        approvedBy: formData.approvedBy || undefined,
        version: 1,
        previousVersions: [],
        attachments: [],
        createdBy: currentUser.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        archived: false,
        deleted: false
      };

      const savedMemo = await addMemo(memoData);

      // Upload images if any
      if (uploadedImages.length > 0) {
        await uploadImages(savedMemo.id);
      }

      toast.success(`Memo ${status === 'Draft' ? 'saved as draft' : 'published'} successfully!`);
      navigate("/policies");
    } catch (error) {
      console.error(`Failed to ${status === 'Draft' ? 'save draft' : 'publish'} memo:`, error);
      toast.error(`Failed to ${status === 'Draft' ? 'save draft' : 'publish'} memo`);
    } finally {
      setIsSaving(false);
    }
  };

  const uploadImages = async (memoId: string) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

    const formData = new FormData();

    for (const image of uploadedImages) {
      formData.append('files', image.file);
      formData.append('captions', image.caption);
    }

    try {
      // 3. Send all files in one API call.
      const response = await fetch(`${API_BASE}/api/memos/${memoId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload files');
      }

      const result = await response.json();
      console.log('Files uploaded successfully:', result);

      // Optional but recommended: You can now update the captions
      // using the attachment data returned in `result.attachments`.
      // For example, you could loop through `result.attachments` and make
      // calls to your `updateImageCaption` endpoint.

    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error(error instanceof Error ? error.message : 'Some files failed to upload');
    }

    // for (const image of uploadedImages) {
    //   const formData = new FormData();
    //   formData.append('file', image.file);
    //   formData.append('caption', image.caption);
    //   formData.append('memoId', memoId);

    //   try {
    //     const response = await fetch(`${API_BASE}/api/memos/${memoId}/upload`, {
    //       method: 'POST',
    //       headers: {
    //         'Authorization': `Bearer ${localStorage.getItem('token')}`
    //       },
    //       body: formData
    //     });

    //     if (!response.ok) {
    //       throw new Error('Failed to upload image');
    //     }
    //   } catch (error) {
    //     console.error('Error uploading image:', error);
    //     toast.error('Some images failed to upload');
    //   }
    // }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    const newImages: UploadedImage[] = [];

    Array.from(files).forEach(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 5MB.`);
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported image type.`);
        return;
      }

      newImages.push({
        file,
        preview: URL.createObjectURL(file),
        caption: ''
      });
    });

    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const updateImageCaption = (index: number, caption: string) => {
    setUploadedImages(prev =>
      prev.map((img, i) => i === index ? { ...img, caption } : img)
    );
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => {
      const imageToRemove = prev[index];
      URL.revokeObjectURL(imageToRemove.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  if (previewMode) {
    return (
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setPreviewMode(false)} className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold leading-tight">Preview: {formData.subject || "Untitled"}</h1>
              <p className="text-sm text-muted-foreground">This is how the memo will appear to users.</p>
            </div>
          </div>
          <div className="w-full md:w-auto">
            <div className="grid grid-cols-2 md:flex md:items-center gap-2">
              <Button variant="outline" className="w-full" onClick={() => setPreviewMode(false)}>
                Back to Edit
              </Button>
              <Button variant="outline" className="w-full" onClick={() => handleSave('Draft')} disabled={isSaving}>
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? "Saving..." : "Save Draft"}
              </Button>
              <Button className="w-full col-span-2 md:col-auto" onClick={() => handleSave('Active')} disabled={isSaving}>
                <Send className="h-4 w-4 mr-2" />
                {isSaving ? "Publishing..." : "Publish"}
              </Button>
            </div>
          </div>
        </div>

        {/* Memo Content Card */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-1">
              <CardTitle className="text-xl md:text-2xl">{formData.subject || "Untitled Memo"}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {formData.number} • Version 1 • Effective: {formData.effectiveDate ? new Date(formData.effectiveDate).toLocaleDateString() : "Not set"}
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Main Content */}
            <div className="prose prose-sm md:prose-base max-w-none text-foreground">
              <div dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '') || "<p>No content provided.</p>" }} />
            </div>

            {/* Image Attachments */}
            {uploadedImages.length > 0 && (
              <div className="space-y-4 pt-8 border-t">
                <h3 className="text-lg font-semibold">Attachments</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                      <img
                        src={image.preview}
                        alt={image.caption || `Attachment ${index + 1}`}
                        className="w-full h-48 object-cover"
                      />
                      {image.caption && (
                        <p className="text-sm text-muted-foreground p-3 bg-muted/50">{image.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/policies">
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">New Memo</h1>
            <p className="text-sm text-muted-foreground">Create a new memo document</p>
          </div>
        </div>

        <div className="w-full md:w-auto">
          <div className="grid grid-cols-2 md:flex md:items-center gap-2">
            <Button variant="outline" className="w-full" onClick={() => setPreviewMode(true)}>
              <Eye className="h-4 w-4 mr-2" /> Preview
            </Button>
            <Button variant="outline" className="w-full" onClick={() => handleSave('Draft')} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" /> {isSaving ? "Saving..." : "Save Draft"}
            </Button>
            <Button className="w-full col-span-2 md:col-auto" onClick={() => handleSave('Active')} disabled={isSaving}>
              <Send className="h-4 w-4 mr-2" /> {isSaving ? "Publishing..." : "Publish"}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form Column */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Memo Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Memo Number and Effective Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="number">Memo Number</Label>
                  <Input id="number" value={formData.number} disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="effectiveDate">Effective Date *</Label>
                  <Input id="effectiveDate" type="date" value={formData.effectiveDate} onChange={(e) => handleEffectiveDateChange(e.target.value)} className={errors.effectiveDate ? "border-red-500" : ""} />
                  {errors.effectiveDate && <p className="text-sm text-red-500">{errors.effectiveDate}</p>}
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Enter memo subject" className={errors.subject ? "border-red-500" : ""} />
                {errors.subject && <p className="text-sm text-red-500">{errors.subject}</p>}
              </div>

              {/* Expiry and Approved By */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="expiryDate">Expiry Date</Label>
                  <Input id="expiryDate" type="date" value={formData.expiryDate} onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })} min={formData.effectiveDate || undefined} disabled={!formData.effectiveDate} className={errors.expiryDate ? "border-red-500" : ""} />
                  {errors.expiryDate && <p className="text-sm text-red-500">{errors.expiryDate}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="approvedBy">Approved By</Label>
                  <Select value={formData.approvedBy} onValueChange={(value) => setFormData({ ...formData, approvedBy: value })}>
                    <SelectTrigger className={errors.approvedBy ? "border-red-500" : ""}><SelectValue placeholder="Select approver" /></SelectTrigger>
                    <SelectContent>{approvers.map((user) => (<SelectItem key={user.id} value={user.id}>{user.name} - {user.userRole}</SelectItem>))}</SelectContent>
                  </Select>
                  {errors.approvedBy && <p className="text-sm text-red-500">{errors.approvedBy}</p>}
                </div>
              </div>

              {/* Signature Requirement */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requireSignature"
                  checked={formData.requireSignature}
                  onChange={(e) => setFormData({ ...formData, requireSignature: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="requireSignature">Require digital signature from users</Label>
              </div>
            </CardContent>
          </Card>

          {/* Content Section */}
          <Card>
            <CardHeader>
              <CardTitle>Memo Content *</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea id="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Enter the detailed memo content..." className={`min-h-[300px] ${errors.content ? "border-red-500" : ""}`} />
              {errors.content && <p className="text-sm text-red-500">{errors.content}</p>}
            </CardContent>
          </Card>

          {/* Image Upload Section */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Images className="h-5 w-5" /> Image Attachments</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/50 hover:bg-muted/80">
                <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
                  <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground"><span className="font-semibold">Click to upload</span> or drag & drop</p>
                  <p className="text-xs text-muted-foreground">PNG, JPG, GIF up to 5MB</p>
                </div>
                <input id="image-upload" type="file" className="hidden" multiple accept="image/*" onChange={handleImageUpload} />
              </label>
              {uploadedImages.length > 0 && (
                <div className="space-y-3">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4 p-3 border rounded-lg">
                      <img src={image.preview} alt={`Upload ${index + 1}`} className="w-full sm:w-24 h-24 object-cover rounded flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium truncate">{image.file.name}</p>
                        <Input placeholder="Add caption (optional)" value={image.caption} onChange={(e) => updateImageCaption(index, e.target.value)} />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeImage(index)} className="text-destructive self-start sm:self-center"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* AI Enhancement Preview */}
          {aiState.showPreview && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="text-sm text-purple-800">
                  AI Enhancement Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-purple-700">
                  <p className="font-medium mb-2">Enhanced {aiState.currentField}:</p>
                  <div className="bg-white p-3 rounded border max-h-40 overflow-y-auto">
                    {aiState.currentField === 'content' ? (
                      <pre className="whitespace-pre-wrap text-xs">{aiState.result}</pre>
                    ) : (
                      <p className="text-xs">{aiState.result}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAiAccept} className="flex-1">
                    Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleAiCancel} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p><strong>Save as Draft:</strong> Save your work without making it active. Only you can see draft memos.</p>
              <p><strong>Save & Publish:</strong> Make the memo active and available for assignments.</p>
              <p><strong>AI Enhancement:</strong> Use Alt+P to polish or Alt+G to generate content.</p>
              <p><strong>Images:</strong> Upload images to enhance your memo content.</p>
            </CardContent>
          </Card>

          {/* Memo Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Memo Details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span>Number: {formData.number}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>Created by: {currentUser?.name || 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Version: 1 (New)</span>
              </div>
              {uploadedImages.length > 0 && (
                <div className="flex items-center gap-2">
                  <Images className="h-4 w-4 text-muted-foreground" />
                  <span>Attachments: {uploadedImages.length}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 